import { RootState } from "@/store";
import { useAppSelector } from "@/store/hooks";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { useCompleteJobTask } from "@labeling-tools/api/hooks";
import { Button, Checkbox, Text, Textarea } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ViewMode } from "@/types";

export const ConfirmationQaSidebarActions = ({
  task,
  mode,
}: {
  task: JobTaskDetailSchema;
  mode?: ViewMode;
}) => {
  const [confirmationFeedback, setConfirmationFeedback] = useState("");
  const [flagLabeler, setFlagLabeler] = useState(false);
  const qaQuestionOneCorrect = useAppSelector(
    (state: RootState) => state.oos.qaQuestionOneCorrect,
  );
  const qaQuestionOneIncorrectParts = useAppSelector(
    (state: RootState) => state.oos.qaQuestionOneIncorrectParts,
  );
  const qaQuestionTwoCorrect = useAppSelector(
    (state: RootState) => state.oos.qaQuestionTwoCorrect,
  );
  const qaQuestionThreeCorrect = useAppSelector(
    (state: RootState) => state.oos.qaQuestionThreeCorrect,
  );
  const allCorrect =
    qaQuestionOneCorrect && qaQuestionTwoCorrect && qaQuestionThreeCorrect;
  const { jobId } = useParams<{ jobId: string }>();
  const queryClient = useQueryClient();

  // init complete job task mutation
  const { mutateAsync: completeJobTask } = useCompleteJobTask(
    jobId || "",
    task.id || "",
  );

  // reset confirmation feedback when task id changes
  useEffect(() => {
    if (task.id) {
      setConfirmationFeedback("");
      setFlagLabeler(false);
    }
  }, [task.id]);

  const handleConfirmationFeedbackChange = (event: any) => {
    setConfirmationFeedback(event.target.value as string);
  };

  const feedbackEntered = confirmationFeedback.trim().length > 0;
  // If Q1 is marked incorrect, the QA reviewer must say which parts were wrong.
  const incorrectPartsMissing =
    !qaQuestionOneCorrect && qaQuestionOneIncorrectParts.length === 0;
  const canSubmit = allCorrect || (feedbackEntered && !incorrectPartsMissing);

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const result = {
      qa_question_one_correct: qaQuestionOneCorrect,
      qa_question_one_incorrect_parts: qaQuestionOneCorrect
        ? []
        : qaQuestionOneIncorrectParts,
      qa_question_two_correct: qaQuestionTwoCorrect,
      qa_question_three_correct: qaQuestionThreeCorrect,
      qa_confirmation_correct:
        qaQuestionOneCorrect && qaQuestionTwoCorrect && qaQuestionThreeCorrect,
      qa_confirmation_feedback: confirmationFeedback,
      flag_labeler: flagLabeler,
    };

    try {
      await completeJobTask({
        result: result,
      });
      // Keep the prefetch's task list fresh so the next prefetch target
      // reflects this completion instead of re-selecting the just-completed
      // task (which is still PENDING in the 10s-stale job-tasks cache).
      queryClient.invalidateQueries({ queryKey: ["job-tasks", jobId] });
      queryClient.invalidateQueries({ queryKey: ["nextPendingTask", jobId] });
    } catch (error) {
      console.error(error);
    }
  };

  if (mode === ViewMode.CONFIRMATION_QA_FEEDBACK) {
    return task?.result?.qa_confirmation_feedback?.length ? (
      <div className="text-left mt-[20px]">
        <Text fw={600}>QA Feedback:</Text>
        <Text>{task?.result?.qa_confirmation_feedback}</Text>
      </div>
    ) : null;
  }

  return (
    <div className="mt-auto">
      <Textarea
        autosize
        minRows={8}
        required
        label="QA Feedback"
        placeholder="Enter any feedback for the original user of this task."
        value={confirmationFeedback}
        onChange={handleConfirmationFeedbackChange}
        style={{ marginBottom: 15 }}
      />
      <Checkbox
        label="Flag for labeler review?"
        checked={flagLabeler}
        onChange={(event) => setFlagLabeler(event.currentTarget.checked)}
        style={{ marginBottom: 15 }}
      />
      <Button fullWidth onClick={handleSubmit} disabled={!canSubmit}>
        Submit
      </Button>
    </div>
  );
};
